import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import L from "leaflet";
import { useFloorData, useMarkerData } from "./floorData";
import { useTypesHelper } from "./useTypesHelper";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { useWorkplaceRequests } from "../../../api/graphql/useWorkplaceRequests";
import { useRoomsRequests } from "../../../api/graphql/rooms/useRoomsRequests";
import { generateTooltipContent } from "../../../lib/helpers/leaflet";
import { LabelAlignment } from "../../../api/graphql/rooms/rooms";

import { Popup } from "../MarkerPopup/Popup";
import { floorPlanHelpers } from "./floorPlanHelpers";
import { getPoiIcon } from "./poiMarkersHelper";
import { FloorFields, FloorPOI } from "../../../api/graphql/floors/floors";
import { FloorChildrenType } from "../FloorChildrenList/FloorChildrenDefaultList";
import { normalizeTooltipScaleFactor } from "../../../lib/helpers/calculateTopOffset";

export type MarkerSettings = {
  scaleFactor?: number;
  showRoomMarkers?: boolean;
  activeDraw?: boolean;
  hasContrastTooltips?: boolean;
};

export enum MarkerStatuses {
  ASSIGNED = 1,
  BLOCKED = 2,
  RESERVED = 3,
  AVAILABLE = 4,
}

const weightValue = 2;
const borderColor = "#156C79";
const fillColor = "#B9D3D7";
const fillOpacity = 0.85;
const tooltipOffsetBase = 1;

export const useMarker = (data: FloorFields, settings?: MarkerSettings) => {
  const { getFloorEntityLeafletIcon, getScaledIcon } = floorPlanHelpers();
  const [allowMarkerDrag, setAllowMarkerDrag] = useState(true);
  const { roomsOfFloor, desksOfFloor, wayfindersfFloor } = useFloorData(data);
  const { user } = useAuthContext();
  const { markerData, cirleData, polygonalData } = useMarkerData(
    roomsOfFloor,
    desksOfFloor,
    wayfindersfFloor,
    settings
  );
  const { updatePOI } = useWorkplaceRequests();
  const { updateRoomCircle, updatePolygonalLabelAlignment } =
    useRoomsRequests();

  const markersCacheRef = useRef<L.Marker[]>([]);
  const circleCacheRef = useRef<L.Circle[]>([]);
  const polygonCacheRef = useRef<L.Polygon[]>([]);
  const floorPOICacheRef = useRef<L.Marker[]>([]);

  const { methodTypes } = useTypesHelper();

  // Helper function to attach event listeners to popup radio buttons
  const attachPopupEventListeners = (
    layer: L.Circle | L.Polygon,
    roomId: string,
    shapeType: "circle" | "polygon"
  ) => {
    layer.on("popupopen", () => {
      setTimeout(() => {
        const radioButtons = document.querySelectorAll(
          'input[name="vertical-room-label-alignment"]'
        );
        radioButtons.forEach((radio) => {
          radio.addEventListener("change", async (e) => {
            const target = e.target as HTMLInputElement;
            const newAlignment = target.value as LabelAlignment;

            try {
              if (shapeType === "circle" && layer instanceof L.Circle) {
                const { lat, lng } = layer.getLatLng();

                await updateRoomCircle({
                  id: roomId,
                  tenantId: user?.customerid || "",
                  circleShape: {
                    latitude: lat,
                    longitude: lng,
                    radius: layer.getRadius(),
                    labelAlignment: newAlignment,
                  },
                });
              } else if (shapeType === "polygon") {
                await updatePolygonalLabelAlignment(
                  roomId,
                  newAlignment,
                  user?.customerid || ""
                );
              }
              // Close and reopen popup to refresh with new alignment
              layer.closePopup();
            } catch (error) {
              console.error("Error updating label alignment:", error);
            }
          });
        });
      }, 100);
    });
  };

  const shapes = useMemo<{
    _circles: L.Circle[];
    _polygonal: L.Polygon[];
  }>(() => {
    // clear cached circles
    circleCacheRef.current.forEach((cachedCircle) => {
      cachedCircle.remove();
    });

    polygonCacheRef.current.forEach((cachedPolygon) => {
      cachedPolygon.remove();
    });

    const _circles = cirleData.flat().map((floorEntity: any) => {
      const newCircle: L.Circle = L.circle(floorEntity?.latlng, {
        color: borderColor,
        fillColor: fillColor,
        fillOpacity: fillOpacity,
        radius: floorEntity?.radius,
        weight: weightValue,
        attribution:
          floorEntity?.id +
          "-innerGoGetIDSplitter-" +
          floorEntity?.name +
          "-innerGoGetIDSplitter-" +
          (floorEntity?.labelAlignment || "center"),
      }).bindPopup(
        ReactDOMServer.renderToString(
          <Popup
            name={floorEntity?.name}
            id={floorEntity?.id}
            type={floorEntity?.type}
            location={floorEntity?.location ? floorEntity.location : ""}
            user={floorEntity?.user ? floorEntity.user : ""}
            tooltipAlignment
            labelAlignment={floorEntity?.labelAlignment}
            // onClick={() => deleteMarker(floorEntity.id)}
          />
        )
      );

      // Attach event listeners for radio buttons
      attachPopupEventListeners(newCircle, floorEntity?.id, "circle");

      return newCircle;
    });

    const _polygonal = polygonalData.flat().map((floorEntity: any) => {
      let newPolygon: L.Polygon | L.Rectangle;

      if (floorEntity.latlng[0]?.type === "polygon") {
        newPolygon = L.polygon(floorEntity?.latlng, {
          color: borderColor,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          weight: weightValue,
          attribution:
            floorEntity?.id +
            "-innerGoGetIDSplitter-" +
            floorEntity?.name +
            "-innerGoGetIDSplitter-" +
            (floorEntity?.labelAlignment || "center"),
        });
      } else {
        newPolygon = L.rectangle(floorEntity?.latlng, {
          color: borderColor,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          weight: weightValue,
          attribution:
            floorEntity?.id +
            "-innerGoGetIDSplitter-" +
            floorEntity?.name +
            "-innerGoGetIDSplitter-" +
            (floorEntity?.labelAlignment || "center"),
        });
      }

      newPolygon.bindPopup(
        ReactDOMServer.renderToString(
          <Popup
            name={floorEntity?.name}
            id={floorEntity?.id}
            type={floorEntity?.type}
            location={floorEntity?.location ? floorEntity.location : ""}
            user={floorEntity?.user ? floorEntity.user : ""}
            tooltipAlignment
            labelAlignment={floorEntity?.labelAlignment}
            // onClick={() => deleteMarker(floorEntity.id)}
          />
        )
      );

      // Attach event listeners for radio buttons
      attachPopupEventListeners(newPolygon, floorEntity?.id, "polygon");

      return newPolygon;
    });

    circleCacheRef.current = _circles;
    polygonCacheRef.current = _polygonal;

    return { _circles, _polygonal };
  }, [data]);

  const poiMarkers = useMemo<L.Marker[]>(() => {
    // clear cached floor POIs
    floorPOICacheRef.current.forEach((cachedPOI) => {
      cachedPOI.remove();
    });

    if (!data?.floorPOI) {
      return [];
    }

    const _poiMarkers = data.floorPOI
      ?.filter((poiMarker) => poiMarker.marker !== null)
      .map((poi: FloorPOI) => {
        const newMarker: L.Marker = L.marker(
          [poi.marker?.latitude, poi?.marker.longitude],
          {
            icon: getPoiIcon(poi.type),
            draggable: true,
            attribution: poi?.id,
            alt: "PoiMarker",
          }
        );

        newMarker.on("dragend", async (e) => {
          try {
            setAllowMarkerDrag(false);

            let { lat, lng } = e.target.getLatLng();

            await updatePOI({
              id: poi.id,
              floorId: data.id,
              marker: {
                latitude: lat === 0 ? 1 : lat,
                longitude: lng === 0 ? 1 : lng,
              },
            });

            return;
          } catch (error: any) {
            console.error("Error updating marker position:", error);
          } finally {
            setAllowMarkerDrag(true);
          }
        });

        return newMarker;
      });

    floorPOICacheRef.current = _poiMarkers;

    return _poiMarkers;
  }, [data]);

  const markers = useMemo<L.Marker[]>(() => {
    // clear cached markers
    markersCacheRef.current.forEach((cachedMarker) => {
      cachedMarker.remove();
    });

    const _markers = markerData.flat().map((floorEntity: any) => {
      const newMarker: L.Marker = L.marker(floorEntity?.latlng, {
        icon: getFloorEntityLeafletIcon(floorEntity, "floor"),
        draggable: true,
        attribution: floorEntity?.id,
        alt: floorEntity?.type,
        title: floorEntity?.name,
      }).bindPopup(
        ReactDOMServer.renderToString(
          <Popup
            name={floorEntity?.name}
            id={floorEntity?.id}
            type={floorEntity?.type}
            location={floorEntity?.location ? floorEntity.location : ""}
            user={floorEntity?.user ? floorEntity.user : ""}
            isBlocked={floorEntity?.isBlocked}
            // onClick={() => deleteMarker(floorEntity.id)}
          />
        )
      );

      newMarker.on("dragend", async (e) => {
        try {
          setAllowMarkerDrag(false);

          let { lat, lng } = e.target.getLatLng();
          let methodName =
            methodTypes[
              e.target.getIcon().options.attribution as FloorChildrenType
            ];

          await methodName({
            id: e.target.getAttribution(),
            marker: {
              latitude: lat === 0 ? 1 : lat,
              longitude: lng === 0 ? 1 : lng,
            },
            tenantId: user?.customerid || "",
          });

          return;
        } catch (error: any) {
          console.error("Error updating marker position:", error);
        } finally {
          setAllowMarkerDrag(true);
        }
      });

      return newMarker;
    });

    markersCacheRef.current = _markers;

    return _markers;
  }, [data]);

  // apply scale factor
  useEffect(() => {
    const rollback: (() => void)[] = [];

    const updateTooltipAndIcon = (marker: L.Marker, isPOI: boolean = false) => {
      const currentIcon = marker.getIcon();

      if (currentIcon && settings?.scaleFactor) {
        marker.setIcon(
          getScaledIcon(
            currentIcon as L.Icon,
            settings.scaleFactor,
            currentIcon.options.attribution === "wayfinder"
          )
        );

        if (!isPOI) {
          const tooltipScaleFactor = normalizeTooltipScaleFactor(
            settings.scaleFactor
          );
          const tooltip = marker.getTooltip();
          if (tooltip && marker.options.title) {
            marker.unbindTooltip();
            marker
              .bindTooltip(
                generateTooltipContent(
                  marker.options.title,
                  settings.scaleFactor,
                  settings?.hasContrastTooltips,
                  true
                ),
                {
                  permanent: true,
                  direction: tooltip.options.direction,
                  opacity: 1,
                  offset: [
                    tooltipOffsetBase * tooltipScaleFactor,
                    tooltipOffsetBase * tooltipScaleFactor,
                  ],
                  className: tooltip.options.className,
                }
              )
              .openTooltip();
          }

          rollback.push(() => {
            marker.setIcon(currentIcon);
            if (tooltip && marker.options.title) {
              marker.unbindTooltip();
              marker.bindTooltip(
                generateTooltipContent(
                  marker.options.title,
                  1,
                  settings?.hasContrastTooltips,
                  true
                ),
                tooltip.options
              );
            }
          });
        } else {
          rollback.push(() => {
            marker.setIcon(currentIcon);
          });
        }
      }
    };

    poiMarkers.forEach((marker) => updateTooltipAndIcon(marker, true));
    markers.forEach((marker) => updateTooltipAndIcon(marker, false));

    return () => {
      rollback.forEach((f) => f());
    };
  }, [markers, poiMarkers, settings, getScaledIcon]);

  // disable dragging of markers until BE finishes execution
  // and then enable dragging again
  useEffect(() => {
    markers.forEach((marker) => {
      if (!marker?.dragging) {
        return;
      }

      if (allowMarkerDrag) {
        marker?.dragging?.enable();
      } else {
        marker?.dragging?.disable();
      }
    });

    poiMarkers.forEach((marker) => {
      if (!marker?.dragging) {
        return;
      }

      if (allowMarkerDrag) {
        marker?.dragging?.enable();
      } else {
        marker?.dragging?.disable();
      }
    });
  }, [allowMarkerDrag]);

  const openMarkerPopup = (id: string) => {
    const clickedMarker = markers.find(
      (marker) => marker.options.attribution === id
    );

    if (clickedMarker) {
      if (clickedMarker.isPopupOpen()) {
        clickedMarker.closePopup();
      } else {
        clickedMarker.openPopup();
      }
    }
  };

  const openShapePopup = (id: string, type: "circle" | "polygonal") => {
    if (type === "circle") {
      const clickedCircle = shapes._circles.find(
        (circle) =>
          circle.options.attribution?.split("-innerGoGetIDSplitter-")[0] === id
      );

      if (clickedCircle) {
        if (clickedCircle.isPopupOpen()) {
          clickedCircle.closePopup();
        } else {
          clickedCircle.openPopup();
        }
      }

      return;
    }

    const clickedPolygon = shapes._polygonal.find(
      (polygon) =>
        polygon.options.attribution?.split("-innerGoGetIDSplitter-")[0] === id
    );

    if (clickedPolygon) {
      if (clickedPolygon.isPopupOpen()) {
        clickedPolygon.closePopup();
      } else {
        clickedPolygon.openPopup();
      }
    }
  };

  return {
    markers,
    poiMarkers,
    shapes,
    roomsOfFloor,
    desksOfFloor,
    wayfindersfFloor,
    openMarkerPopup,
    openShapePopup,
  };
};
